import { useEffect, useRef, type RefObject } from 'react'
import type { Task, Section } from '../../../lib/db/models'
import { isInsideTaskDetails } from '../utils/taskDetailsFocus'

/** A render segment: optional section header followed by a group of tasks */
interface RenderSegment {
  section: Section | null
  tasks: Task[]
  taskCount: number
}

/** ⇧A darüber, ⇧B darunter, ⇧F ganz oben. */
const SECTION_KEYS: Partial<Record<string, 'above' | 'below' | 'top'>> = {
  KeyA: 'above',
  KeyB: 'below',
  KeyF: 'top',
}

interface NavItem {
  type: 'task' | 'section'
  id: string
}

interface UseProjectKeyboardShortcutsParams {
  savedName: string
  tasks: Task[] | undefined
  creatingPosition: 'top' | 'bottom' | 'above' | null
  creatingAfterTaskId: string | null
  /** Die gezeichneten Segmente — `activeSegmentRef` zählt in deren Indizes (#274). */
  segments: RenderSegment[]
  activeSegmentRef: RefObject<number>
  navClearSelection: () => void
  navSelectTask: (taskId: string) => void
  startCreating: (position: 'top' | 'bottom' | 'above') => void
  startCreatingAfter: (taskId: string) => void
  /**
   * Neuer Abschnitt — `⇧A` darüber, `⇧B` darunter, `⇧F` ganz oben; das Menü
   * wie `⇧B`. Wohin genau und ob markierte Aufgaben mitkommen,
   * entscheidet die Seite.
   */
  onAddSection: (where: 'above' | 'below' | 'top') => void
  handleDuplicate: () => void
  focusedNavIndex: number | null
  flatNavList: NavItem[]
  /** When true, skip shortcuts that target sections or create "above" tasks (e.g. Quick Notes). */
  sectionsReadonly?: boolean
  /**
   * ⇧A/⇧B/⇧F und „Neuer Abschnitt" im Menü legen nichts an. Zeigt die Liste nur einen
   * Abschnitt, stünde der neue unsichtbar am Projektende und bliebe ohne Namen.
   */
  addSectionDisabled?: boolean
  /**
   * Die Liste zeigt nur einen Abschnitt: `F` legt dann wie `A` oben in diesem an — „ganz oben ohne Abschnitt"
   * läge außerhalb der Ansicht (#274, Sven).
   */
  singleSection?: boolean
  /** When false, global listener is skipped entirely. Used by TabStack to
   *  keep inactive-but-mounted ProjectPages from intercepting shortcuts. */
  isActive?: boolean
  /**
   * ⌘N und Menü „Neue Aufgabe“ ohne Fokus — fehlt es, unten im aktiven Teil. Kurz notiert legt so oben in
   * „Letzte 7 Tage“ an, wie die Pille (#247).
   */
  onNewTask?: () => void
  /**
   * ⌘N und Menü „Neue Aufgabe“ ganz in der Hand der Seite, auch bei markierter Aufgabe oder markiertem Kopf. Die
   * Projektseite legt so an wie der Knopf der Pille: immer oben (#272). Die Seite liest die Markierung selbst.
   */
  onNewTaskCommand?: () => void
}

export function useProjectKeyboardShortcuts({
  savedName,
  tasks,
  creatingPosition,
  creatingAfterTaskId,
  segments,
  activeSegmentRef,
  navClearSelection,
  navSelectTask,
  startCreating,
  startCreatingAfter,
  onAddSection,
  handleDuplicate,
  focusedNavIndex,
  flatNavList,
  sectionsReadonly = false,
  addSectionDisabled = false,
  singleSection = false,
  isActive = true,
  onNewTask,
  onNewTaskCommand,
}: UseProjectKeyboardShortcutsParams) {
  const isCreating = !!creatingPosition || !!creatingAfterTaskId

  // Refs for volatile values — read inside handler to avoid re-registration on every Dexie update
  const savedNameRef = useRef(savedName)
  savedNameRef.current = savedName
  const tasksRef = useRef(tasks)
  tasksRef.current = tasks
  const isCreatingRef = useRef(isCreating)
  isCreatingRef.current = isCreating
  const segmentsRef = useRef(segments)
  segmentsRef.current = segments
  const focusedNavIndexRef = useRef(focusedNavIndex)
  focusedNavIndexRef.current = focusedNavIndex
  const handleDuplicateRef = useRef(handleDuplicate)
  handleDuplicateRef.current = handleDuplicate
  const flatNavListRef = useRef(flatNavList)
  flatNavListRef.current = flatNavList
  const addSectionDisabledRef = useRef(addSectionDisabled)
  addSectionDisabledRef.current = addSectionDisabled
  const singleSectionRef = useRef(singleSection)
  singleSectionRef.current = singleSection
  // ⌘N und Menü ohne Fokus: der Weg der Seite, sonst unten im aktiven Teil.
  const newTaskRef = useRef<() => void>(() => {})
  newTaskRef.current = onNewTask ?? (() => startCreating('bottom'))
  const newTaskCommandRef = useRef(onNewTaskCommand)
  newTaskCommandRef.current = onNewTaskCommand

  // Keyboard shortcuts: "A" = add top, "B" = add bottom, Cmd/Ctrl+N = add bottom,
  // ⇧A/⇧B/⇧F = new section above/below/at the top
  useEffect(() => {
    if (!isActive) return
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (!savedNameRef.current.trim()) return
      if (isCreatingRef.current) return
      if (tasksRef.current === undefined) return // still loading
      // Don't intercept keys when a dropdown/context menu is open
      if (document.querySelector('[role="menu"]')) return

      // Cmd/Ctrl+D — duplicate selected items. Nicht ⇧⌘D: Das ist die Dauer (#257); der Mac meldet es als „d“.
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key === 'd') {
        e.preventDefault()
        handleDuplicateRef.current()
        return
      }

      // Cmd/Ctrl+N — der Weg der Seite, wenn sie einen vorgibt (Projekt: wie die Pille, #272); sonst unter der
      // markierten Aufgabe, oben im markierten Abschnitt oder ohne Markierung `newTaskRef`.
      // Nicht mit Umschalt: ⌘⇧N legte bis zum 01.10.2026 einen Abschnitt an und ist
      // durch ⇧A/⇧B/⇧F abgelöst — es soll nicht stattdessen eine Aufgabe anlegen.
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'n') {
        e.preventDefault()
        if (newTaskCommandRef.current) {
          newTaskCommandRef.current()
          return
        }
        const focusIdx = focusedNavIndexRef.current
        if (focusIdx !== null) {
          const item = flatNavListRef.current[focusIdx]
          if (item?.type === 'task') {
            startCreatingAfter(item.id)
            return
          }
          if (item?.type === 'section') {
            // activeSegmentRef already points to this section's segment
            navClearSelection()
            startCreating('top')
            return
          }
        }
        navClearSelection()
        newTaskRef.current()
        return
      }

      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      // Die Tasten der **Aufgaben-Details** gehören ihnen (CONTEXT.md): Dort
      // steht der Fokus meist auf einem Knopf, an dem der INPUT/TEXTAREA-Wächter
      // oben vorbeigeht.
      if (isInsideTaskDetails(e.target)) return
      if (e.ctrlKey || e.metaKey || e.altKey) return

      // ⇧A / ⇧B / ⇧F — neuer Abschnitt darüber, darunter, ganz oben: der Spiegel
      // zu A, B und F. Legt nie eine Aufgabe an; wo kein Abschnitt geht (Kurz
      // notiert, nur ein Abschnitt im Bild), passiert nichts.
      const sectionWhere = e.shiftKey ? SECTION_KEYS[e.code] : undefined
      if (sectionWhere) {
        e.preventDefault()
        if (!sectionsReadonly && !addSectionDisabledRef.current) onAddSection(sectionWhere)
        return
      }

      if (e.key === 'a' || e.key === 'A') {
        e.preventDefault()
        // Read focused item BEFORE clearing selection (clearing resets activeSegmentRef)
        const focusIdx = focusedNavIndexRef.current
        const focusedItem = focusIdx !== null ? flatNavListRef.current[focusIdx] : null
        navClearSelection()
        // Section focused → always create at top of that segment
        if (focusedItem?.type === 'section') {
          startCreating('top')
          return
        }
        const segs = segmentsRef.current
        const seg = segs[activeSegmentRef.current] ?? segs[0]
        const segHasTasks = (seg?.tasks.length ?? 0) > 0
        const pos = segHasTasks ? 'top' : 'bottom'
        startCreating(pos)
      }
      if (e.key === 'b' || e.key === 'B') {
        e.preventDefault()
        navClearSelection()
        startCreating('bottom')
      }
      if ((e.key === 'f' || e.key === 'F') && !sectionsReadonly) {
        e.preventDefault()
        navClearSelection()
        // When active segment is the unsectioned "above" area, use A-key logic
        // (top/bottom) which has a more polished CMD+Enter continue flow.
        // Zeigt die Liste nur einen Abschnitt, ebenso: „ganz oben" läge außerhalb der Ansicht (#274).
        const segs = segmentsRef.current
        const seg = segs[activeSegmentRef.current] ?? segs[0]
        if (seg && (!seg.section || singleSectionRef.current)) {
          const segHasTasks = (seg.tasks.length ?? 0) > 0
          startCreating(segHasTasks ? 'top' : 'bottom')
        } else {
          startCreating('above')
        }
      }
    }
    window.addEventListener('keydown', handleGlobalKeyDown)
    return () => window.removeEventListener('keydown', handleGlobalKeyDown)
  }, [isActive, onAddSection, navClearSelection, startCreating, startCreatingAfter, activeSegmentRef, sectionsReadonly])

  /*
    Die Menübefehle kommen ans ganze Fenster (`mainWindow.webContents.send`),
    und der TabStack hält versteckte Tabs montiert. Angemeldet wird darum nur
    im sichtbaren Tab — sonst legte „Neuer Abschnitt" in jedem offenen Projekt
    einen an, und „Duplizieren" verdoppelte auch die Auswahl versteckter Tabs.
  */
  // IPC: create new task (triggered by File > New Task menu click)
  useEffect(() => {
    if (!isActive || !window.api?.receive) return
    const unsubscribe = window.api.receive('create-new-task', () => {
      if (!savedNameRef.current.trim() || isCreatingRef.current || tasksRef.current === undefined) return
      if (newTaskCommandRef.current) {
        newTaskCommandRef.current()
        return
      }
      const focusIdx = focusedNavIndexRef.current
      if (focusIdx !== null) {
        const item = flatNavListRef.current[focusIdx]
        if (item?.type === 'task') {
          startCreatingAfter(item.id)
          return
        }
        if (item?.type === 'section') {
          navClearSelection()
          startCreating('top')
          return
        }
      }
      navClearSelection()
      newTaskRef.current()
    })
    return () => {
      unsubscribe?.()
    }
  }, [isActive, navClearSelection, startCreating, startCreatingAfter])

  // IPC: create new section (triggered by File > New Section menu click)
  useEffect(() => {
    if (!isActive || !window.api?.receive) return
    const unsubscribe = window.api.receive('create-new-section', () => {
      if (!addSectionDisabledRef.current) onAddSection('below')
    })
    return () => {
      unsubscribe?.()
    }
  }, [isActive, onAddSection])

  // When a creating entry is open and the user clicks on an existing task,
  // capture the task ID from the DOM (via [data-task-id]) BEFORE the creating
  // entry unmounts and the layout shifts. Then select that task after all
  // handlers have settled.
  useEffect(() => {
    if (!isCreating) return

    const handlePointerDown = (e: PointerEvent) => {
      const taskEl = (e.target as HTMLElement).closest?.('[data-task-id]')
      if (!taskEl) return
      const taskId = taskEl.getAttribute('data-task-id')
      if (!taskId) return

      // Schedule after creating entry unmounts and click-outside handlers settle
      setTimeout(() => {
        navSelectTask(taskId)
      }, 50)
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => document.removeEventListener('pointerdown', handlePointerDown, true)
  }, [isCreating, navSelectTask])
}
